import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { format } from 'date-fns'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { reminderAPI, habitAPI } from '../lib/supabase'

const { FiBell, FiPlus, FiEdit2, FiTrash2, FiClock, FiRepeat, FiVolume2, FiSmartphone, FiToggleLeft, FiToggleRight, FiXCircle } = FiIcons

const Reminders = () => {
  const [reminders, setReminders] = useState([])
  const [habits, setHabits] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingReminder, setEditingReminder] = useState(null)
  const [notificationPermission, setNotificationPermission] = useState('default')
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    time: '09:00',
    days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    habit_id: null,
    type: 'daily',
    sound: true,
    vibration: true,
    is_active: true
  })

  const weekDays = [
    { id: 'monday', name: 'Mon' },
    { id: 'tuesday', name: 'Tue' },
    { id: 'wednesday', name: 'Wed' },
    { id: 'thursday', name: 'Thu' },
    { id: 'friday', name: 'Fri' },
    { id: 'saturday', name: 'Sat' },
    { id: 'sunday', name: 'Sun' }
  ]

  useEffect(() => {
    loadData()
    checkNotificationPermission()
  }, [])

  const loadData = async () => {
    try {
      const [remindersResult, habitsResult] = await Promise.all([
        reminderAPI.getReminders(),
        habitAPI.getHabits()
      ])

      setReminders(remindersResult.data || [])
      setHabits(habitsResult.data || [])
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission)
    }
  }

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      setNotificationPermission(permission)
      if (permission === 'granted') {
        new Notification('DreamForge Notifications Enabled!', {
          body: 'You\'ll now receive conquest reminders.',
          icon: '/favicon.ico'
        })
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.title.trim()) return

    try {
      let result
      if (editingReminder) {
        result = await reminderAPI.updateReminder(editingReminder.id, formData)
        setReminders(prev => prev.map(r => r.id === editingReminder.id ? result.data : r))
      } else {
        result = await reminderAPI.createReminder(formData)
        setReminders(prev => [result.data, ...prev])
      }

      setShowForm(false)
      setEditingReminder(null)
      resetForm()
    } catch (error) {
      console.error('Error saving reminder:', error)
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      time: '09:00',
      days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
      habit_id: null,
      type: 'daily',
      sound: true,
      vibration: true,
      is_active: true
    })
  }

  const handleEdit = (reminder) => {
    setEditingReminder(reminder)
    setFormData({
      title: reminder.title,
      message: reminder.message,
      time: reminder.time,
      days: reminder.days || [],
      habit_id: reminder.habit_id,
      type: reminder.type,
      sound: reminder.sound,
      vibration: reminder.vibration,
      is_active: reminder.is_active
    })
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this reminder?')) return

    try {
      await reminderAPI.deleteReminder(id)
      setReminders(prev => prev.filter(r => r.id !== id))
    } catch (error) {
      console.error('Error deleting reminder:', error)
    }
  }

  const toggleReminderStatus = async (reminder) => {
    try {
      const updated = await reminderAPI.updateReminder(reminder.id, {
        ...reminder,
        is_active: !reminder.is_active
      })
      setReminders(prev => prev.map(r => r.id === reminder.id ? updated.data : r))
    } catch (error) {
      console.error('Error toggling reminder:', error)
    }
  }

  const testNotification = () => {
    if (notificationPermission === 'granted') {
      new Notification('Test Notification', {
        body: 'This is how your conquest reminders will look!',
        icon: '/favicon.ico'
      })
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-dominance-gradient flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-forge-400/30 border-t-forge-400 rounded-full animate-spin mb-4"></div>
          <h3 className="text-xl font-bold text-forge-400 mb-2">Loading Reminders</h3>
          <p className="text-forge-300">Setting up your conquest alerts...</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-dominance-gradient">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center space-x-2 mb-4">
            <SafeIcon icon={FiBell} className="text-2xl text-forge-400" />
            <h1 className="text-3xl font-bold bg-forge-gradient bg-clip-text text-transparent">
              Never Miss
            </h1>
          </div>
          <p className="text-forge-300 mb-6 px-4">
            Stay on track with intelligent conquest reminders
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setShowForm(true)}
              className="w-full sm:w-auto bg-forge-gradient text-shadow-950 px-6 py-3 rounded-xl font-semibold hover:shadow-power transition-all duration-300"
            >
              <div className="flex items-center justify-center space-x-2">
                <SafeIcon icon={FiPlus} />
                <span>Create Reminder</span>
              </div>
            </button>
            {notificationPermission !== 'granted' && (
              <button
                onClick={requestNotificationPermission}
                className="w-full sm:w-auto bg-gradient-to-r from-shadow-800 to-shadow-700 text-forge-300 px-6 py-3 rounded-xl font-semibold border border-forge-400/30 hover:shadow-lg transition-all duration-300"
              >
                Enable Notifications
              </button>
            )}
          </div>
        </motion.div>

        {/* Notification Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-2xl shadow-power border border-forge-400/30 p-6 mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                notificationPermission === 'granted'
                  ? 'bg-gradient-to-r from-success-400 to-success-500'
                  : 'bg-gradient-to-r from-gray-400 to-slate-500'
              }`}>
                <SafeIcon icon={FiSmartphone} className="text-white text-xl" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-forge-400">
                  Notifications: {notificationPermission === 'granted' ? 'Enabled' : 'Disabled'}
                </h3>
                <p className="text-forge-300 text-sm">
                  {notificationPermission === 'granted'
                    ? 'You\'ll receive conquest reminders as scheduled'
                    : 'Enable notifications to receive timely reminders'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {notificationPermission === 'granted' && (
                <button
                  onClick={testNotification}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:shadow-lg transition-all duration-300 text-sm"
                >
                  Test
                </button>
              )}
              {notificationPermission !== 'granted' && (
                <button
                  onClick={requestNotificationPermission}
                  className="px-4 py-2 bg-forge-gradient text-shadow-950 rounded-lg font-medium hover:shadow-lg transition-all duration-300 text-sm"
                >
                  Enable
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Reminders List */}
        {reminders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 bg-forge-gradient rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-power">
              <SafeIcon icon={FiBell} className="text-shadow-950 text-2xl" />
            </div>
            <h3 className="text-xl font-bold text-forge-400 mb-4">No Reminders Yet</h3>
            <p className="text-forge-300 mb-8 px-4">
              Set up intelligent reminders to maintain conquest consistency.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-forge-gradient text-shadow-950 px-8 py-3 rounded-xl font-semibold hover:shadow-power transition-all duration-300"
            >
              Create First Reminder
            </button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {reminders.map((reminder, index) => {
                const linkedHabit = habits.find(h => h.id === reminder.habit_id)

                return (
                  <motion.div
                    key={reminder.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-gradient-to-r from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-2xl shadow-power border p-6 hover:shadow-dominance transition-all duration-300 ${
                      reminder.is_active ? 'border-forge-400/30' : 'border-gray-400/30 opacity-60'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="relative flex-shrink-0">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl shadow-lg ${
                            reminder.is_active
                              ? 'bg-forge-gradient'
                              : 'bg-gradient-to-r from-gray-400 to-slate-500'
                          }`}>
                            {linkedHabit ? linkedHabit.icon : '🔔'}
                          </div>
                          {reminder.is_active && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-success-400 to-success-500 rounded-full flex items-center justify-center">
                              <SafeIcon icon={FiBell} className="text-white text-xs" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-bold text-forge-400 truncate">{reminder.title}</h3>
                            <span className="px-3 py-1 bg-forge-gradient text-shadow-950 rounded-full text-sm font-medium whitespace-nowrap shadow-forge">
                              {reminder.time}
                            </span>
                          </div>
                          <p className="text-forge-300 mb-3 text-sm">{reminder.message}</p>
                          <div className="flex flex-wrap items-center gap-4 text-sm">
                            <div className="flex items-center space-x-1">
                              <SafeIcon icon={FiRepeat} className="text-forge-400" />
                              <span className="text-forge-300">
                                {reminder.days?.length === 7 ? 'Daily' : `${reminder.days?.length || 0} days/week`}
                              </span>
                            </div>
                            {linkedHabit && (
                              <div className="flex items-center space-x-1">
                                <span className="text-forge-400">for</span>
                                <span className="px-2 py-1 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-400 rounded-full text-xs font-medium border border-blue-400/30">
                                  {linkedHabit.title}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center space-x-2">
                              {reminder.sound && <SafeIcon icon={FiVolume2} className="text-forge-400 text-sm" />}
                              {reminder.vibration && <SafeIcon icon={FiSmartphone} className="text-forge-400 text-sm" />}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <button
                          onClick={() => toggleReminderStatus(reminder)}
                          className={`p-2 rounded-lg transition-all duration-300 ${
                            reminder.is_active
                              ? 'text-success-400 hover:bg-success-400/20'
                              : 'text-gray-400 hover:bg-gray-400/20'
                          }`}
                        >
                          <SafeIcon icon={reminder.is_active ? FiToggleRight : FiToggleLeft} className="text-xl" />
                        </button>
                        <button
                          onClick={() => handleEdit(reminder)}
                          className="p-2 text-forge-400 hover:text-forge-200 hover:bg-forge-400/20 rounded-lg transition-all duration-200"
                        >
                          <SafeIcon icon={FiEdit2} className="text-sm" />
                        </button>
                        <button
                          onClick={() => handleDelete(reminder.id)}
                          className="p-2 text-forge-400 hover:text-danger-400 hover:bg-danger-400/20 rounded-lg transition-all duration-200"
                        >
                          <SafeIcon icon={FiTrash2} className="text-sm" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Reminder Form Modal */}
        <AnimatePresence>
          {showForm && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 100 }}
                className="bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-sm w-full sm:max-w-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto border border-forge-400/30"
              >
                <div className="bg-forge-gradient p-4 text-shadow-950 sm:rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold">
                      {editingReminder ? 'Edit Reminder' : 'Create Reminder'}
                    </h2>
                    <button
                      onClick={() => {
                        setShowForm(false)
                        setEditingReminder(null)
                        resetForm()
                      }}
                      className="p-2 hover:bg-shadow-900/20 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiXCircle} />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-forge-300 mb-2">
                        Reminder Title *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 text-forge-200 placeholder-forge-300/50"
                        placeholder="Morning meditation reminder"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-forge-300 mb-2">
                        Time
                      </label>
                      <input
                        type="time"
                        value={formData.time}
                        onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                        className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 text-forge-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-forge-300 mb-2">
                      Link to Habit (Optional)
                    </label>
                    <select
                      value={formData.habit_id || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, habit_id: e.target.value || null }))}
                      className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 text-forge-200"
                    >
                      <option value="">Select a habit (optional)</option>
                      {habits.map(habit => (
                        <option key={habit.id} value={habit.id}>
                          {habit.icon} {habit.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-forge-300 mb-2">
                      Message
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      rows="3"
                      className="w-full px-4 py-3 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 resize-none bg-shadow-800/50 text-forge-200 placeholder-forge-300/50"
                      placeholder="Time to dominate your habit! 💪"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-forge-300 mb-3">
                      Repeat on Days
                    </label>
                    <div className="grid grid-cols-7 gap-2">
                      {weekDays.map(day => (
                        <button
                          key={day.id}
                          type="button"
                          onClick={() => {
                            const newDays = formData.days.includes(day.id)
                              ? formData.days.filter(d => d !== day.id)
                              : [...formData.days, day.id]
                            setFormData(prev => ({ ...prev, days: newDays }))
                          }}
                          className={`p-3 rounded-xl text-center transition-all duration-300 ${
                            formData.days.includes(day.id)
                              ? 'bg-forge-gradient text-shadow-950 shadow-forge'
                              : 'bg-shadow-800/50 hover:bg-shadow-700/50 border border-forge-400/20 text-forge-200'
                          }`}
                        >
                          <div className="text-sm font-medium">{day.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 bg-shadow-800/50 rounded-xl">
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiVolume2} className="text-forge-400" />
                        <div>
                          <p className="font-medium text-forge-200 text-sm">Sound</p>
                          <p className="text-xs text-forge-300">Play notification sound</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, sound: !prev.sound }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          formData.sound ? 'bg-forge-400' : 'bg-gray-400'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.sound ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-shadow-800/50 rounded-xl">
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiSmartphone} className="text-forge-400" />
                        <div>
                          <p className="font-medium text-forge-200 text-sm">Vibration</p>
                          <p className="text-xs text-forge-300">Vibrate device</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, vibration: !prev.vibration }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          formData.vibration ? 'bg-forge-400' : 'bg-gray-400'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.vibration ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-6 border-t border-forge-400/20">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false)
                        setEditingReminder(null)
                        resetForm()
                      }}
                      className="px-6 py-3 border border-forge-400/30 text-forge-300 rounded-xl hover:bg-shadow-700/50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-3 bg-forge-gradient text-shadow-950 rounded-xl hover:shadow-power transition-all duration-300"
                    >
                      {editingReminder ? 'Update' : 'Create'} Reminder
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default Reminders